// Off Book PWA — backend API client.
//
// Backend URL is set via VITE_OFFBOOK_API at build time; defaults to the
// local dev backend for `npm run dev`.

// Defensive: env values can arrive with trailing junk (e.g. instructional
// text pasted into the Cloudflare dashboard var). Take the first whitespace-
// delimited token and drop any trailing slash so `${API_BASE}/path` is valid.
const RAW_API_BASE: string =
  (import.meta as any).env?.VITE_OFFBOOK_API ?? "http://localhost:8000";
const API_BASE: string = RAW_API_BASE.trim().split(/\s+/)[0].replace(/\/+$/, "");

export interface Methodology {
  id: string;
  display_name: string;
  camp: string;
  legal_tier: number;
  reads_psychology: boolean;
}

// --- parse-script types ---------------------------------------------------

export interface Cue {
  idx: number;
  character: string;
  text: string;
  scene_idx: number;
}

export interface ParsedProject {
  script_name: string;
  characters: string[];
  cues: Cue[];
}

export interface ParseScriptResponse {
  project: ParsedProject;
  warnings: string[];
  source: string;
  raw_text_length: number;
}

// --- extract-toolkit types (welcome-arc contract, per Rhys 2026-05-31) ----

export type SignalType = "tool" | "teacher" | "school" | "method" | "register";
export type Confidence = "high" | "medium" | "low" | "insufficient";
export type LowReason = "neutral" | "unregistered" | "vague" | null;

export interface Signal {
  type: SignalType;
  text: string;
  methodology_hint: string | null;
}

export interface Placement {
  methodology_id: string;
  confidence: number;
  evidence: string[];
}

export interface ToolkitProfile {
  signals: Signal[];
  placements: Placement[];
  lineage_hints: string[];
  overall_confidence: Confidence;
  followup_question: string | null;
  low_reason: LowReason;
  unregistered_hints: string[];
}

export interface ExtractToolkitResponse {
  profile: ToolkitProfile;
  usage: Record<string, number>;
  cost_usd_estimate: number;
}

// --- quiz types (the real onboarding front, per Rhys spawn 2026-06-03) -----
//
// Replaces the free-text extract-toolkit placeholder. The 5-question quiz
// places the actor deterministically into one of 5 lineages and returns
// sourced lineage + toolkit content (every factual string carries source_ref
// per §11). No circular copy: the reveal text is authored data, not model
// output.

export interface QuizOption {
  key: string; // "A".."E"
  label: string;
  body: string;
  lineage: string | null; // lineage_id voted, or null (baseline funnel)
}

export interface QuizQuestion {
  id: string; // "q1".."q5"
  prompt: string;
  options: QuizOption[];
  source_ref: string;
}

export interface QuizPayload {
  questions: QuizQuestion[];
  source_ref: string;
}

export interface ToolkitTool {
  name: string;
  gist: string;
  source_ref: string;
}

// Per-chain citation flag (Cael citation-fold 2026-06-05): 'green' = primary
// corpus citable; 'yellow' = method-faithful, post-1970 limb (Chubbuck /
// Practical Aesthetics / Roy London) — primaries pending mind-map research.
// Surface rule: citations live behind the optional "?" / Archive, never the
// front reveal line.
export interface LineageChain {
  lineage_id: string;
  chain_string: string;
  citation_state: "green" | "yellow";
  source_ref: string;
}

// A tool as it appears in the aggregator's union (tools[] / browsable_library[])
// — carries the four-door routing tags + answered_for + per-lineage chains.
export interface ToolWithMeta extends ToolkitTool {
  lineage_chains: LineageChain[];
  // question_id ("q2" / "q4") when the actor's answer literally named this
  // tool; null when the tool is bundled from a voted-for lineage but not
  // literally chosen. Drives Cael's "You reached for this." tag.
  answered_for: string | null;
  room: "journal" | "drills" | "run_lines" | "stats";
  kind: "breakdown" | "character" | "drill_method" | "drill_classic";
}

export interface Lineage {
  id: string;
  recognition_lead: string;
  tradition_name: string;
  tradition_short_name: string; // bare display form ("Meisner"), Cael templates
  lineage_story: string;
  practitioners: string[];
  toolkit: ToolkitTool[];
  analyzer_methodology_id: string;
  analyzer_gap: string | null;
  source_ref_lineage: string;
  source_ref_practitioners: string;
}

// As emitted inside QuizResult.lineages[] — carries tools_from_here for the
// per-lineage view (signature tools of that lineage). The shape of an entry
// inside the lineages array (additive on top of Lineage).
export interface LineageInResult extends Lineage {
  tools_from_here: ToolWithMeta[];
}

export interface QuizResult {
  // New additive surface (post-A1 aggregator, brief amended-3b):
  signal_case: "mixed" | "convergent" | "unsure";
  lineages: LineageInResult[]; // voted-for lineages, tally desc (empty for unsure)
  tools: ToolWithMeta[]; // dedup union of voted lineages' toolkits
  browsable_library: ToolWithMeta[]; // full 5-lineage corpus deduped — A3 panel
  // Existing back-compat surface:
  lineage_id: string;
  lineage: Lineage;
  is_baseline_funnel: boolean;
  analyzer_methodology_id: string;
  analyzer_gap: string | null;
  tally: Record<string, number>;
  source_refs: string[];
  cost_usd_estimate: number;
}

// --- analyze types --------------------------------------------------------

export interface AnalyzeRequest {
  project: ParsedProject;
  methodology_id: string;
  selected_characters: string[];
  secondary_id?: string | null;
  backend_name?: string;
  skip_arc?: boolean;
}

export interface AnalysisResult {
  backend: string;
  methodology: {
    primary: string;
    secondary: string | null;
    selected_characters: string | string[];
  };
  script_name: string;
  scene_analyses: Array<{
    scene_idx: number;
    scene_summary: string;
    beats: Array<{
      beat_id: string;
      name: string;
      cue_start: number;
      cue_end: number;
      intention_shift: string;
    }>;
    cue_analysis: Array<unknown>;
    character_updates: Array<{
      character: string;
      state_after_scene: string;
      pursuit_next?: string;
      key_lines: string[];
    }>;
    hot_person_questions?: Array<{
      character: string;
      question: string;
      hot_person: string | null;
    }>;
  }>;
  arc: {
    characters: Array<{
      character: string;
      arc_summary: string;
      emotional_journey: string;
      actor_notes?: string;
      key_moments: Array<{ scene_idx: number; moment: string }>;
      suggested_voice_tags: Record<string, string>;
    }>;
    structural_summary: string;
  } | null;
  usage: Record<string, number>;
  cost_usd_estimate: number | null;
}

// --- HTTP plumbing --------------------------------------------------------

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, init);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status} on ${path}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export async function getHealth(): Promise<{ status: string; version: string }> {
  return http("/health");
}

export async function getMethodologies(): Promise<Methodology[]> {
  const r = await http<{ methodologies: Methodology[] }>("/methodologies");
  return r.methodologies;
}

export async function getImportedProject(
  projectId: string,
): Promise<ParseScriptResponse & { project_id: string }> {
  return http<ParseScriptResponse & { project_id: string }>(
    `/parse-script/imported/${encodeURIComponent(projectId)}`,
  );
}

// --- async parse-script-job (per-page background parse for big scans) ----

export interface ParseScriptStage {
  stage_id: string;
  label: string;
  index: number;
  total: number;
  [k: string]: unknown;
}

export interface ParseScriptJobStatus {
  job_id: string;
  status: "running" | "done" | "error";
  stage: ParseScriptStage | null;
  result: ParseScriptResponse | null;
  error: string | null;
}

export async function postParseScriptJob(
  file: File,
  projectName?: string,
): Promise<{ job_id: string; status: string }> {
  const fd = new FormData();
  fd.append("file", file);
  if (projectName) fd.append("project_name", projectName);
  const res = await fetch(`${API_BASE}/parse-script-job`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status} on /parse-script-job: ${body}`);
  }
  return res.json();
}

export async function getParseScriptJob(
  jobId: string,
): Promise<ParseScriptJobStatus> {
  return http<ParseScriptJobStatus>(`/parse-script-job/${encodeURIComponent(jobId)}`);
}

export async function postParseScript(
  file: File,
  projectName?: string,
): Promise<ParseScriptResponse> {
  const fd = new FormData();
  fd.append("file", file);
  if (projectName) fd.append("project_name", projectName);
  const res = await fetch(`${API_BASE}/parse-script`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status} on /parse-script: ${body}`);
  }
  return res.json();
}

export async function getQuiz(): Promise<QuizPayload> {
  return http<QuizPayload>("/quiz");
}

export async function postQuizResult(
  answers: Record<string, string>,
): Promise<QuizResult> {
  return http<QuizResult>("/quiz-result", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ answers }),
  });
}

export async function postExtractToolkit(
  actorText: string,
  priorProfile?: ToolkitProfile | null,
): Promise<ExtractToolkitResponse> {
  return http<ExtractToolkitResponse>("/extract-toolkit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      actor_text: actorText,
      prior_profile: priorProfile ?? null,
    }),
  });
}

export async function postAnalyze(req: AnalyzeRequest): Promise<AnalysisResult> {
  return http<AnalysisResult>("/analyze", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

// --- v2 endpoints (day-0.1) --------------------------------------------------

export interface InspectResult {
  condition: "text-extractable" | "clean-scan" | "dense-scan" | "image-only-no-text" | "garbled" | "unsupported";
  page_count: number;
  est_seconds: number;
  est_cost_usd: number;
  recommended_path: "/parse-script" | "/parse-script-job" | null;
  warnings: string[];
  details: Record<string, unknown>;
}

export async function postInspect(file: File): Promise<InspectResult> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/parse-script/inspect`, { method: "POST", body: fd });
  if (!res.ok) throw new Error(`HTTP ${res.status} on /parse-script/inspect: ${await res.text()}`);
  return res.json();
}

export interface DefineResult {
  word: string;
  definition: string;
  kind: "common" | "proper_noun" | "period_term" | "foreign";
  register_note: null;
}

export async function postDefine(
  word: string, context_sentence?: string, context_script?: string,
): Promise<DefineResult> {
  return http<DefineResult>("/define", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ word, context_sentence, context_script }),
  });
}

export interface ResearchKeyword {
  term: string;
  kind: "proper_noun" | "period_term" | "foreign" | "technical";
  first_line_idx: number;
  first_line_excerpt: string;
}

export interface ResearchKeywordsResult {
  keywords: ResearchKeyword[];
  usage: Record<string, number>;
  cost_usd_estimate: number;
}

export async function postResearchKeywords(
  project: ParsedProject, selected_character?: string,
): Promise<ResearchKeywordsResult> {
  return http<ResearchKeywordsResult>("/research-keywords", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ project, selected_character }),
  });
}

export interface Annotation {
  id: string;
  project_id: string;
  kind: "objective" | "beat" | "given_circumstances" | "substitution" | "note" | "photo" | "voice_memo" | "idea";
  body: string;
  line_range: [number, number];
  scene_idx: number | null;
  media_url: string | null;
  private: boolean;
  ts_utc: string;
}

export async function postAnnotation(a: Omit<Annotation, "id" | "ts_utc" | "private">): Promise<Annotation> {
  return http<Annotation>("/annotations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(a),
  });
}

export async function getAnnotations(project_id: string): Promise<{ annotations: Annotation[] }> {
  return http<{ annotations: Annotation[] }>(`/annotations/${encodeURIComponent(project_id)}`);
}

// --- methodology-aware prompt sequences (per the journal-and-script-markup spec) ---

export type WorkflowMoment =
  | "first_read" | "second_read" | "scene_breakdown"
  | "per_rehearsal" | "pre_performance" | "post_performance";

export interface MethodologyPrompt {
  tool_number: number | null;
  sequence:    number;
  locked:      boolean;
  kind:        string;
  private:     boolean;
  label:       string;
  text:        string;
}

export async function getMethodologyPrompts(
  methodology_id: string, moment: WorkflowMoment,
): Promise<{ methodology_id: string; workflow_moment: WorkflowMoment; prompts: MethodologyPrompt[] }> {
  return http(`/methodology-prompts/${encodeURIComponent(methodology_id)}/${encodeURIComponent(moment)}`);
}

// --- takes (rehearsal recordings + per-line manifest) ---------------------

export interface TakeManifestEntry {
  line_id: number;
  start_ms: number;
  end_ms: number;
}

export interface Take {
  project_id: string;
  take_id: string;
  audio_url: string;        // absolute URL (API_BASE prepended)
  audio_bytes: number;
  audio_mime: string;
  audio_ext: string;
  manifest: TakeManifestEntry[];
  duration_ms: number | null;
  created_at: string;
}

function _absolutize(envelope: any): Take {
  const url: string = envelope.audio_url;
  return {
    ...envelope,
    audio_url: url.startsWith("http") ? url : `${API_BASE}${url}`,
  } as Take;
}

export async function postTake(
  projectId: string,
  audioBlob: Blob,
  manifest: TakeManifestEntry[],
  durationMs?: number,
): Promise<Take> {
  const fd = new FormData();
  const ext = audioBlob.type.includes("mp4") ? "mp4"
            : audioBlob.type.includes("webm") ? "webm"
            : audioBlob.type.includes("m4a") ? "m4a"
            : "webm";
  fd.append("audio", new File([audioBlob], `take.${ext}`, { type: audioBlob.type }));
  fd.append("manifest", JSON.stringify(manifest));
  if (durationMs !== undefined) fd.append("duration_ms", String(Math.floor(durationMs)));
  const res = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/take`, {
    method: "POST", body: fd,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return _absolutize(await res.json());
}

export async function getTake(projectId: string, takeId: string): Promise<Take> {
  const envelope = await http<any>(
    `/projects/${encodeURIComponent(projectId)}/take/${encodeURIComponent(takeId)}`,
  );
  return _absolutize(envelope);
}

export { API_BASE };
